import {difference} from 'lodash-es';
import {useCallback, useReducer} from 'react';
import {z} from 'zod';

export const selectionDataSchema = z.object({
  all: z.boolean(),
  touched: z.string().array(),
});
export type SelectionData = z.infer<typeof selectionDataSchema>;

export type SelectionAction2 =
  | {
      type: 'TOGGLE_ONE';
      payload: {
        id: string;
      };
    }
  | {
      type: 'TOGGLE_MANY';
      payload: {
        ids: string[]; // list of ids to select
        checked: boolean;
      };
    }
  | {
      type: 'TOGGLE_ALL';
      payload: {
        checked: boolean;
      };
    }
  | {type: 'RESET'};

const defaultInitialState: SelectionData = {
  all: false,
  touched: [],
};

const reducer = (
  state: SelectionData,
  action: SelectionAction2
): SelectionData => {
  switch (action.type) {
    case 'TOGGLE_ONE': {
      return {
        all: state.all,
        touched: state.touched.includes(action.payload.id)
          ? state.touched.filter((id) => id !== action.payload.id)
          : [...state.touched, action.payload.id],
      };
    }

    case 'TOGGLE_MANY': {
      if (action.payload.checked) {
        const deduped = new Set([...state.touched, ...action.payload.ids]);
        return {
          all: state.all,
          touched: Array.from(deduped),
        };
      } else {
        const filtered = difference(state.touched, action.payload.ids);
        return {
          all: state.all,
          touched: filtered,
        };
      }
    }

    case 'TOGGLE_ALL': {
      return {
        all: action.payload.checked,
        touched: [],
      };
    }

    case 'RESET': {
      return {...defaultInitialState};
    }

    default:
      throw new Error('Action not supported');
  }
};

export interface SelectionHelpers2<T = string> {
  isIndeterminate: boolean;
  dispatch: React.Dispatch<SelectionAction2>;
  getSelectedIds: (fullList: T[], accessor?: (item: T) => string) => string[];
  getSelectedCount: (totalCount: number) => number;
  getIsSelected: (id: string) => boolean;
}

export function useSelection<T = string>(options?: {
  initialState?: SelectionData;
  external?: {
    state: SelectionData;
    dispatch: (next: SelectionData) => unknown;
  };
}): [SelectionData, SelectionHelpers2<T>] {
  const [hookState, hookDispatch] = useReducer(
    reducer,
    options?.initialState ?? defaultInitialState
  );

  const externalDispatch = useCallback(
    (action: SelectionAction2) => {
      if (!options?.external) throw new Error('Hook not configured properly');
      return options.external.dispatch(reducer(options.external.state, action));
    },
    [options?.external]
  );

  const state = options?.external ? options.external.state : hookState;

  const getSelectedIds = useCallback(
    (fullList: T[], accessor?: (item: T) => string) => {
      if (!accessor && !listElementsAreStrings(fullList)) {
        throw new Error(
          'Selection function requires list of ids or an accessor function'
        );
      }

      const base = state.all
        ? accessor
          ? fullList.map(accessor)
          : // type guard show be working here, not sure what issue is
            [...(fullList as string[])]
        : [];

      if (base.length) {
        return difference(base, state.touched);
      } else {
        return [...state.touched];
      }
    },
    [state.all, state.touched]
  );

  const getSelectedCount = useCallback(
    (totalCount: number) => {
      if (state.all) {
        return totalCount - state.touched.length;
      } else {
        return state.touched.length;
      }
    },
    [state.all, state.touched]
  );

  const getIsSelected = useCallback(
    (id: string) => {
      const isTouched = state.touched.includes(id);
      return isTouched ? !state.all : state.all;
    },
    [state.all, state.touched]
  );

  return [
    state,
    {
      dispatch: options?.external ? externalDispatch : hookDispatch,
      getSelectedIds,
      getSelectedCount,
      getIsSelected,
      isIndeterminate: !!state.touched.length,
    },
  ];
}

function listElementsAreStrings(list: unknown[]): list is string[] {
  if (!list.length) return false;
  return typeof list[0] === 'string';
}
